import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertBox,
  PhoneInput,
  SelectInput,
  TextInput,
  Button,
  Step,
} from "@life-line-lab/components-react";
import errorHandler from "Utils/error-handler";
import { generateSteps } from "Helpers/generate-stepper-steps";
import { isNil, omitBy, omit, isEmpty, pick } from "lodash";
import { useParams } from "react-router-dom";

// HOOKS
import { useSession } from "Hooks/index";

// ANTD
import { Col, Row } from "antd";

// VALIDATOR
import { ParcelOrderInfoSchema } from "./parcel-order-information-validation";
import useStatesList from "Hooks/states-option.hook";

interface ParcelOrderInfo {
  info?: any;
  onNext?: any;
  customerDetails?: any;
  customerList?: any;
  fetchCustomerLocation?: any;
  countryOption?: any;
  handleFetchCustomerList?: () => void;
  locationList?: any;
  handleFetchEstimatedDeliveryDays:any
  parentInput?: any;
  isEdit: any;
  companyDetail: any;
  isRoleCustomer: any;
}

const ParcelOrderInfo: React.FC<ParcelOrderInfo> = ({
  info,
  companyDetail,
  onNext,
  isRoleCustomer,
  customerDetails,
  customerList,
  handleFetchCustomerList,
  locationList,
  countryOption,
  fetchCustomerLocation,
  handleFetchEstimatedDeliveryDays,
  parentInput,
  isEdit,
}) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { type } = useParams<{ type: string }>();
  const initialInput = {
    senderType: "INDIVIDUAL",
    customer: null,
    individualName: null,
    customerLocation: null,
    emailAddress: "",
    contactNo: {
      dialCode: "60",
      number: "",
    },
    street1: "",
    postcode: "",
    city: "",
    state: null,
    country: "MY",
  };

  const initialError = {};
  const initialSteps: Step[] = generateSteps<Step>(4);
  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);
  let [current, setCurrent] = useState<number>(0);
  const [steps, setSteps] = useState<any>(initialSteps);
  const stateOption = useStatesList(input.country);

  const { org } = useSession();

  useEffect(() => {
    getCustomerDetails();
  }, [customerDetails]);

  useEffect(() => {
    if (!isEmpty(parentInput)) {  
      let filteredParentInput = pick(parentInput, [
        "senderData.customerType",
        "customer",
        "customerLocation",
        "emailAddress",
      ]);

      // mismatch definition between backend and frontend
      // -- customer (frontend) = individualName (backend)
      // -- individualName (frontend) = senderData.contact.fullName (backend)

      // awkward mapping
      // POST (RECEIVE) - senderType: senderData.customerType
      // POST (SEND) - senderType: senderType
      setInput({
        customer: isRoleCustomer ? org._id : null,
        ...initialInput,
        ...filteredParentInput,
        contactNo: parentInput?.senderContact,
        individualName: parentInput?.individualName ?? parentInput?.senderData?.contact.fullName ,
        senderType: parentInput?.senderData ? parentInput.senderData.customerType : "INDIVIDUAL",
        ...parentInput.senderAddress,
      });
    } else {
      if (isRoleCustomer) {
        setInput({ ...input, customer: org._id });
      }
    }
  }, [parentInput]);

  // useEffect(() => {
  //   if (!isNil(locationList)) {
  //     const location = locationList.find(loc => loc.locations._id == input.customerLocation)
  //     console.log(location)
  //     setInput({
  //       ...input,
  //       street1: location?.locations.address.street1,
  //       postcode: location?.locations.address.postcode,
  //       city: location?.locations.address.city,
  //       state: location?.locations.address.state,
  //       country: location?.locations.address.country,
  //     })
  //   }
  // }, [input.customerLocation])

  const getCustomerDetails = () => {
    // handleInputWithRole() ;

    if (isRoleCustomer == true) {
      if (customerDetails) {
        setInput((prevInput) => ({
          ...prevInput,
          ...customerDetails,
        }));
      }
    }
  };

  const handleInputWithRole = () => {
    if (isRoleCustomer == true) {
      return (
        <Col xs={24} sm={24} md={12}>
          <TextInput
            isRequired={true}
            label={t("label.customer").toUpperCase()}
            placeholder={t("txt.enterName")}
            name="customerID"
            value={companyDetail}
            onChange={handleFieldChange}
            isReadonly={true}
          />
          <AlertBox text={error.customer} />
        </Col>
      );
    } else {
      return (
        <>
          <Col xs={24} sm={24} md={12}>
            <SelectInput
              showSearch
              options={[
                {
                  label: "Individual",
                  value: "INDIVIDUAL",
                },
                {
                  label: "Company",
                  value: "COMPANY",
                },
              ]}
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
              }
              name="senderType"
              placeholder={t("placeholder.individualOrCompany")}
              label={t("label.senderType")}
              isRequired={true}
              value={input.senderType}
              onChange={(value) => handleSelectChange(value, "senderType")}
              isReadonly={isEdit && true}
            />
            <AlertBox text={error.senderType} />
          </Col>

          <Col xs={24} sm={24} md={12}>
            <SelectInput
              showSearch
              options={customerList.map((customer) => {
                return {
                  value: customer._id,
                  label: customer.name ? customer.name : input.individualName,
                };
              })}
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
              }
              name="customerID"
              placeholder={t("placeholder.filterByCustomer")}
              label={t("label.customer").toUpperCase()}
              value={input.customer}
              isRequired={true}
              onChange={(value) => handleSelectChange(value, "customer")}
              isReadonly={isEdit && true}
            />
            <AlertBox text={error.customer} />
          </Col>
        </>
      );
    }
  };

  // useEffect(() => {
  //   handleRoleType();
  // }, [isRoleCustomer]);

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value: string | string[] | {}, name: string) => {
    setError(initialError);

    if (name == "senderType") {
      handleFetchCustomerList(value);
      input.customer = null;
      input.customerLocation = null;
    }

    if (name == "customer") {
      fetchCustomerLocation(value);
      const customer = customerList.find((cust) => cust._id == value);

      input.customerLocation = null;
      setInput({
        ...input,
        [name]: value,
        individualName: customer?.contact.fullName,
        emailAddress: customer?.primaryEmail.trim(),
        contactNo: {
          dialCode: customer?.primaryPhone.countryCode,
          number: customer?.primaryPhone.number,
        },
      });

      return;
    }

    if (name == "country") {
      setInput({ ...input, [name]: value as string, state: undefined });
      input.state = null;
    }

    if (name == "customerLocation") {
      const location = locationList.find((loc) => loc.locations._id == value);
      setInput({
        ...input,
        [name]: value,
        street1: location?.locations.address.street1,
        street2: location?.locations.address.street2 ? location.locations.address.street2 : null,
        postcode: location?.locations.address.postcode,
        city: location?.locations.address.city,
        state: location?.locations.address.state,
        country: location?.locations.address.country,
      });
      return;
    }


    setInput({ ...input, [name]: value });
  };

  const handlePhoneChange = (value: { dialCode: string; number: string }, name: string) => {
    setError(initialError);
    setInput({ ...input, [name]: value });
  };

  const handleSubmit = () => {
    try {
      const valInput = {
        ...input,
      };
      const value: any = ParcelOrderInfoSchema.validateSync(omitBy(valInput, isNil), {
        strict: false,
        abortEarly: true,
        stripUnknown: true,
      });

      value.senderContact = value.contactNo;
      value.senderAddress = {
        street1: value.street1,
        street2: value.street2,
        postcode: value.postcode,
        city: value.city,
        state: value.state,
        country: value.country,
      };
      omit(value, ["street1", "street2", "postcode", "city", "state", "country", "contactNo"]);

      handleFetchEstimatedDeliveryDays(input.customer, value.postcode)

      onNext(value);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError({ ...error, [path]: t(`${value}`, { field: t(`label.${path}`) }) });
    }
  };

  return (
    <React.Fragment>
      {<div className="center header-title">{t("title.senderInformation").toUpperCase()}</div>}

      <Row gutter={[15, 15]} className="mt-50">
        {handleInputWithRole()}
        <Col xs={24} sm={24} md={12}>
          <TextInput
            isRequired={true}
            label={t("label.individualName")}
            placeholder={t("txt.enterName")}
            name="individualName"
            value={input.individualName}
            onChange={handleFieldChange}
          />
          <AlertBox text={error.individualName} />
        </Col>

        <Col xs={24} sm={24} md={12} className="mb-7">
          <SelectInput
            showSearch
            options={locationList?.map((location) => {
              return {
                value: location.locations._id,
                // label: location.locations.locationCode
                label:
                  location.locations.primary == true
                    ? "Primary Address"
                    : location.locations.locationName,
              };
            })}
            filterOption={(inputValue, option) =>
              option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
            }
            name="customerLocation"
            placeholder={t("placeholder.filterByCustomerLocation")}
            label={t("nav.customerLocation")}
            isRequired={true}
            value={input.customerLocation}
            onChange={(value) => handleSelectChange(value, "customerLocation")}
          />
          <AlertBox text={error.customerLocation} />
        </Col>

        <Col xs={24} sm={24} md={12}>
          <TextInput
            isRequired={true}
            label={t("label.emailAddress").toUpperCase()}
            placeholder={t("txt.emailAddressExample")}
            name="emailAddress"
            value={input.emailAddress}
            onChange={handleFieldChange}
          />
          <AlertBox text={error.emailAddress} />
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Col xs={24} sm={24} className="mb-7">
            <PhoneInput
              isRequired={true}
              isError={!!error.contactNo}
              label={t("label.contactNo")}
              name="contactNo"
              value={input.contactNo}
              placeholder={t("txt.enterMobileNo")}
              onChange={(value: any) => handlePhoneChange(value, "contactNo")}
            />
          </Col>
          <AlertBox text={error["contactNo.number"] || error["contactNo.dialCode"]} />
        </Col>

        <Col xs={24} sm={24} md={12}>
          <label className="inp-label inp-label--required">{t("label.senderAddress")}</label>
          <div className="mb-15">
            <TextInput
              isReadonly={true}
              isRequired={true}
              placeholder={t("txt.enterStreetName")}
              value={input.street1}
              name="street1"
              onChange={handleFieldChange}
            />
            <AlertBox text={error.street1} />
          </div>
          <div className="mb-15">
            <TextInput
              isReadonly={true}
              isRequired={true}
              placeholder={t("txt.enterStreetName")}
              value={input.street2}
              name="street2"
              onChange={handleFieldChange}
            />
            <AlertBox text={error.street2} />
          </div>
          <Row gutter={[15, 15]}>
            <Col xs={24} sm={24} md={12} className="mb-0">
              <TextInput
                isReadonly={true}
                isRequired={true}
                placeholder={t("txt.postcode")}
                value={input.postcode}
                name="postcode"
                onChange={handleFieldChange}
              />
              <AlertBox text={t(error.postcode)} />
            </Col>
            <Col xs={24} sm={24} md={12} className="mb-0">
              <TextInput
                isReadonly={true}
                isRequired={true}
                placeholder={t("txt.city")}
                value={input.city}
                name="city"
                onChange={handleFieldChange}
              />
              <AlertBox text={t(error.city)} />
            </Col>
            <Col xs={24} sm={24} md={12} className="mb-0">
              <SelectInput
                showSearch
                options={stateOption}
                filterOption={(inputValue, option) =>
                  option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                }
                value={input.state}
                isReadonly={isNil(input.country) ? true : true}
                name="state"
                placeholder={t("txt.selectState")}
                onChange={(value: string | string[]) => handleSelectChange(value, "state")}
              />
              <AlertBox text={t(error.state)} />
            </Col>
            <Col xs={24} sm={24} md={12} className="mb-0">
              <SelectInput
                showSearch
                isReadonly={true}
                options={countryOption}
                filterOption={(inputValue, option) =>
                  option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                }
                value={input.country}
                name="country"
                placeholder={t("txt.selectCountry")}
                onChange={(value: string | string[]) => handleSelectChange(value, "country")}
              />
              <AlertBox text={error.country} />
            </Col>
          </Row>
        </Col>
      </Row>

      <div className="steps-action center mt-50">
        {current < steps.length - 1 && (
          <Button type="primary" onClick={() => handleSubmit()} label={t("action.next")} />
        )}
      </div>
    </React.Fragment>
  );
};

export default ParcelOrderInfo;
